import { trpc } from '@/App'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { useLocalStorage } from 'usehooks-ts'
import { useCapturePostHogEvent } from './useCapturePosthogEvent'

export const useDowngradeUserPlan = () => {
  const [isDowngradeSuccess, setDowngradeSuccess] = useState<boolean>(false)
  const [isDiscountedSuccess, setDiscountedSuccess] = useState<boolean>(false)
  const [isUpgradeWithDiscountSuccess, setUpgradeWithDiscountSuccess] = useState<boolean>(false)
  const [isActivationSuccess, setActivationSuccess] = useState<boolean>(false)

  const [, setNewReactivation] = useLocalStorage<boolean>('reactivation', false)

  const navigate = useNavigate()
  const { capture } = useCapturePostHogEvent()

  const handleMutationError = (error: { message: string }) => {
    if (error instanceof Error) {
      if (error.message.includes('already present')) {
        toast.error('This account already exists.')
        setTimeout(() => navigate({ to: '/feeds/templates' }), 2000)
      } else {
        toast.error(error.message)
      }
    } else {
      toast.error('An unknown error occurred')
    }
  }

  const handleDowngradeMutationSuccess = (response: { success: boolean }) => {
    if (response?.success) {
      setDowngradeSuccess(true)
      toast.success('Your subscription has been downgraded successfully!')
    }
  }

  const handleDiscountedMutationSuccess = (response: { success: boolean }) => {
    if (response?.success) {
      setDiscountedSuccess(true)
      toast.success('Your subscription has been discounted successfully!')
    }
  }

  const handleUpgradeWithDiscountMutationSuccess = (response: {
    success: boolean
  }) => {
    if (response?.success) {
      setUpgradeWithDiscountSuccess(true)
      toast.success('Your subscription has been upgraded with discount successfully!')
    }
  }

  const handleActivateMutationSuccess = (response: { success: boolean }) => {
    if (response?.success) {
      // capture event for reactivation
      capture('USER_ACCOUNT_REACTIVATED', {
        source: 'campaign',
      })
      setNewReactivation(true)

      setActivationSuccess(true)
      toast.success('Your account has been activated successfully!')
    }
  }

  // For moving from a higher plan to a lower one
  const { isPending: downgradeIsPending, mutate: downgradeUserPlan } = trpc.downgradeUserPlan.useMutation({
    trpc: { context: { idempotent: true } },
    onSuccess: handleDowngradeMutationSuccess,
    onError: handleMutationError,
  })

  // Users who intend to cancel gets the discounted pro plan
  const { isPending: discountedUpgradeIsPending, mutate: discountedUpgrade } = trpc.addCouponToPlan.useMutation({
    onSuccess: handleDiscountedMutationSuccess,
    onError: handleMutationError,
  })

  // Users who intend to upgrade or downgrade on discount
  const { isPending: upgradeDiscountIsPending, mutate: upgradeWithDiscount } =
    trpc.upgradeUserWithActivePlan.useMutation({
      trpc: { context: { idempotent: true } },
      onSuccess: handleUpgradeWithDiscountMutationSuccess,
      onError: handleMutationError,
    })

  // Users who wants to reactivate their account to a free plan
  const { isPending: activateToFreeIsPending, mutate: activateToFree } = trpc.upgradeUserWithNoPlan.useMutation({
    trpc: { context: { idempotent: true } },
    onSuccess: handleActivateMutationSuccess,
    onError: handleMutationError,
  })

  return {
    downgradeUserPlan,
    downgradeIsPending,
    isDowngradeSuccess,
    isActivationSuccess,
    isDiscountedSuccess,
    isUpgradeWithDiscountSuccess,
    discountedUpgradeIsPending,
    upgradeDiscountIsPending,
    activateToFreeIsPending,
    discountedUpgrade,
    activateToFree,
    upgradeWithDiscount,
  }
}
